import React from 'react';
import { buildSupplierVerificationChecklist } from '../../lib/supplierOnboarding';

// "Action required / Unverified" — the supplier's own view of his verification file.
//
// Decided by the owner on 2026-09-16, the day registration opened to the public:
// verification gates only payout (golden rule 2), which left nothing pushing a
// supplier to finish his file. This puts the incentive back through visibility —
// he is told, in his own dashboard, that traders currently see his account without
// the verification badge.
//
// Three things in the copy are deliberate and must not drift:
//   1. The copy frames requests, offers and messaging as what verification UNLOCKS,
//      at the owner's explicit direction on 2026-09-16 after the softer wording was
//      rejected as too weak. Read the constraint honestly before changing it:
//      technically none of the three is gated — canOperate ignores verification and
//      migrations 20260806000003/4 un-gated offers and messaging on purpose. What
//      makes the sentence TRUE is a policy the owner holds, not a check the code
//      runs: RFQ distribution to factories is manual, so an unverified supplier
//      receives no requests exactly as long as he is not sent any. If that policy
//      ever changes, this sentence becomes false and must change with it.
//   2. It never promises the badge appears when he uploads. The badge is gated on
//      `status = verified`, which only an admin sets. The honest sequence is
//      upload → under review → approved → badge, and the card states it.
//   3. It never claims traders see a NEGATIVE "unverified" mark. They see the
//      absence of a badge. An explicit trader-facing label was parked, on purpose.
//
// THREE states, not two. The third was found in the data on the day this shipped:
// of 24 suppliers on record, two sat in 'verification_required' with all eight
// fields filled and had simply never pressed submit. Reading "complete" as "under
// review" would have told those two "nothing is needed from you" while their file
// sat on their own screen, seen by nobody. So:
//   1. file short of the eight fields      → action required, with what is missing
//   2. eight fields filled, never submitted → action required, send it for review
//   3. eight fields filled, with reviewers  → calm; nothing is needed from him

const INK = '#1A1814';
// Red, at the owner's direction on 2026-09-16: the amber card read as optional,
// and verification is not optional. Deep crimson rather than alert-red — this is
// a standing state on his own dashboard, not an error he just triggered.
const AMBER_INK = '#8E1F24';
const AMBER_BG = '#FDF1F0';
const AMBER_LINE = '#EFC9C6';
const CALM_BG = '#F2F5F3';
const CALM_LINE = '#D9E2DB';
const CALM_INK = '#3F6B52';

const T = {
  ar: {
    actionRequired: 'إجراء مطلوب',
    unverified: 'غير موثّق',
    underReview: 'قيد المراجعة',
    headline: 'وثّق حسابك — التوثيق أساس التعامل في مَعبر',
    tradersSee: 'وثّق حسابك لتصلك طلبات التجار، وتقدّم عروضك، وتتواصل معهم مباشرة.',
    daysOne: 'حسابك غير موثّق منذ يوم واحد',
    daysTwo: 'حسابك غير موثّق منذ يومين',
    daysMany: (n) => `حسابك غير موثّق منذ ${n} يومًا`,
    daysToday: 'حسابك غير موثّق',
    sequence: 'بعد رفعك للملفات يراجعها فريق مَعبر، وتظهر الشارة عند اعتمادها.',
    missingTitle: 'الناقص في ملفك',
    progress: (d, t) => `${d} من ${t} مكتملة`,
    cta: 'أكمل ملفك الآن',
    readyHeadline: 'ملفك جاهز — لم يبقَ إلا الإرسال',
    readyBody: 'عبّأت الحقول المطلوبة كلها، لكن ملفك لم يُرسَل للمراجعة بعد — ولن يطّلع عليه أحد قبل أن ترسله. التجار يرون حسابك الآن بدون شارة التوثيق.',
    readyCta: 'أرسل ملفك للمراجعة',
    reviewHeadline: 'ملفك مكتمل وتحت المراجعة',
    lightReviewHeadline: 'تحت المراجعة — لا يلزمك شيء الآن',
    lightReviewBody: 'استلمنا رخصتك التجارية وبيانات التواصل، وهذا ما يلزم لاعتمادك. بمجرد الاعتماد تصل عروضك إلى التاجر. بقية ملف الشركة تُستكمل لاحقًا — وهي مطلوبة قبل استلام المدفوعات فقط.',
    reviewBody: 'استلمنا ملفك كاملًا. تظهر شارة التوثيق للتجار بمجرد اعتماده، ولا يلزمك شيء الآن.',
  },
  en: {
    actionRequired: 'Action required',
    unverified: 'Unverified',
    underReview: 'Under review',
    headline: 'Verify your account — verification is the basis of trading on Maabar',
    tradersSee: 'Verify your account to receive trader requests, submit your offers, and deal with traders directly.',
    daysOne: 'Your account has been unverified for 1 day',
    daysTwo: 'Your account has been unverified for 2 days',
    daysMany: (n) => `Your account has been unverified for ${n} days`,
    daysToday: 'Your account is unverified',
    sequence: 'Once you upload, the Maabar team reviews your file, and the badge appears when it is approved.',
    missingTitle: 'Still missing from your file',
    progress: (d, t) => `${d} of ${t} complete`,
    cta: 'Complete your file now',
    readyHeadline: 'Your file is ready — it just has not been sent',
    readyBody: 'You have filled in every required field, but your file has not been submitted for review yet, and nobody sees it until you send it. Traders currently see your account without the verification badge.',
    readyCta: 'Send your file for review',
    reviewHeadline: 'Your file is complete and under review',
    lightReviewHeadline: 'Under review — nothing needed from you right now',
    lightReviewBody: 'We have your business licence and contact details, which is what we need to approve you. Once approved, your quotes reach the buyer. The rest of your company file can wait — it is only required before you get paid.',
    reviewBody: 'We have your full file. The verification badge appears to traders once it is approved — nothing is needed from you.',
  },
  zh: {
    actionRequired: '需要处理',
    unverified: '未认证',
    underReview: '审核中',
    headline: '请完成认证 — 认证是在 Maabar 开展业务的基础',
    tradersSee: '完成认证后即可接收买家询盘、提交报价并与买家直接洽谈。',
    daysOne: '您的账户已有 1 天未认证',
    daysTwo: '您的账户已有 2 天未认证',
    daysMany: (n) => `您的账户已有 ${n} 天未认证`,
    daysToday: '您的账户未认证',
    sequence: '您上传后由 Maabar 团队审核，通过后认证标识即会显示。',
    missingTitle: '资料中仍缺少',
    progress: (d, t) => `已完成 ${d} / ${t}`,
    cta: '立即完善资料',
    readyHeadline: '资料已齐全 — 只差提交',
    readyBody: '您已填写所有必填项，但资料尚未提交审核，在您提交之前无人会看到。买家目前看到的账户没有认证标识。',
    readyCta: '提交资料以供审核',
    reviewHeadline: '资料已提交，正在审核',
    lightReviewHeadline: '审核中 — 目前无需您操作',
    lightReviewBody: '我们已收到您的营业执照和联系方式，这就是审核所需的全部内容。通过审核后，您的报价将发送给买家。公司资料的其余部分可稍后补充——仅在收款前需要。',
    reviewBody: '我们已收到您的完整资料。通过审核后买家即可看到认证标识，您无需再做任何操作。',
  },
};


// The one lever that sharpens on its own. Everything else on this card says the
// same thing on day 40 as on day 1; this does not, and it is simply true — read
// off the account's own created_at, never invented.
function unverifiedDays(profile) {
  const created = profile?.created_at ? new Date(profile.created_at) : null;
  if (!created || Number.isNaN(created.getTime())) return 0;
  const days = Math.floor((Date.now() - created.getTime()) / 86400000);
  return days > 0 ? days : 0;
}

function daysLine(c, n) {
  if (n <= 0) return c.daysToday;
  if (n === 1) return c.daysOne;
  if (n === 2) return c.daysTwo;
  return c.daysMany(n);
}

function Mark({ tone, onBand }) {
  const red = tone === 'amber';
  const ink = onBand ? '#FFFFFF' : (red ? AMBER_INK : CALM_INK);
  return (
    <span aria-hidden="true" style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 26,
      height: 26,
      flexShrink: 0,
      borderRadius: '50%',
      border: `1.25px solid ${ink}`,
      color: ink,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 16,
      lineHeight: 1,
      paddingBottom: 1,
    }}>!</span>
  );
}


export default function SupplierActionRequiredCard({ profile, lang = 'ar', isUnderReview = false, onVerify, style }) {
  const c = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? { fontFamily: 'var(--font-ar)' } : { fontFamily: 'var(--font-sans)' };
  const checklist = buildSupplierVerificationChecklist(profile || {}, lang);
  // A complete file that was never submitted is still the supplier's move, not ours.
  const readyToSubmit = checklist.isComplete && !isUnderReview;
  // Under review is the one calm state, and it WINS over checklist completeness.
  // The checklist is the eight-field stage-2 (payout) file; since the two-bar
  // model shipped, a supplier can be legitimately under review on the light
  // stage-1 bar with that file still incomplete. Keying the colour off the
  // checklist showed them a red "Action required" one click after
  // /supplier/verify told them "We have everything we need".
  const amber = !isUnderReview;
  const days = unverifiedDays(profile);

  return (
    <div style={{
      background: amber ? AMBER_BG : CALM_BG,
      border: `1px solid ${amber ? AMBER_INK : CALM_LINE}`,
      borderRadius: 16,
      overflow: 'hidden',
      ...style,
    }}>
      {/* A solid band, not a tint. The tinted version read as a suggestion; this is
          the first thing on the dashboard and it is meant to be unmissable. */}
      <div style={{
        background: amber ? AMBER_INK : CALM_INK,
        padding: '11px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexDirection: isAr ? 'row-reverse' : 'row',
      }}>
        <Mark tone={amber ? 'amber' : 'calm'} onBand />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#FFF', letterSpacing: 0.2, ...font }}>
          {amber ? c.actionRequired : c.underReview}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{
          fontSize: 11.5, fontWeight: 700, color: '#FFF',
          border: '1px solid rgba(255,255,255,.55)', borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap', ...font,
        }}>{c.unverified}</span>
      </div>

      <div style={{ padding: '17px 19px 19px' }}>
      <h3 style={{
        margin: 0,
        fontSize: isAr ? 19 : 20,
        fontWeight: 800,
        lineHeight: 1.45,
        color: 'var(--text-primary)',
        ...font,
      }}>{isUnderReview
        ? (checklist.isComplete ? c.reviewHeadline : c.lightReviewHeadline)
        : !checklist.isComplete ? c.headline : readyToSubmit ? c.readyHeadline : c.reviewHeadline}</h3>

      {isUnderReview ? (
        <p style={{ margin: '9px 0 0', fontSize: 13.5, lineHeight: 1.75, color: 'var(--text-secondary)', ...font }}>
          {checklist.isComplete ? c.reviewBody : c.lightReviewBody}
        </p>
      ) : !checklist.isComplete ? (
        <>
          <p style={{ margin: '9px 0 0', fontSize: 14, lineHeight: 1.8, color: 'var(--text-primary)', ...font }}>
            {c.tradersSee}
          </p>

          <p style={{
            margin: '12px 0 0', fontSize: 13, fontWeight: 700, color: AMBER_INK,
            display: 'inline-block', background: '#FBE1DF', border: `1px solid ${AMBER_LINE}`,
            borderRadius: 8, padding: '7px 11px', ...font,
          }}>{daysLine(c, days)}</p>

          {/* progress — the eight fields the submit RPC actually demands */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: AMBER_INK, ...font }}>
                {c.progress(checklist.done, checklist.total)}
              </span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: AMBER_INK }}>
                {checklist.percent}%
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: '#F6DCDA', overflow: 'hidden' }}>
              <div style={{ width: `${checklist.percent}%`, height: '100%', background: '#B23A3F', borderRadius: 999 }} />
            </div>
          </div>

          {checklist.missingLabels.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ margin: '0 0 7px', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', ...font }}>
                {c.missingTitle}
              </p>
              <ul style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px 8px',
              }}>
                {checklist.missingLabels.map((label, i) => (
                  <li key={i} style={{
                    fontSize: 12.5,
                    color: AMBER_INK,
                    background: '#FBE1DF',
                    border: `1px solid ${AMBER_LINE}`,
                    borderRadius: 8,
                    padding: '5px 9px',
                    ...font,
                  }}>{label}</li>
                ))}
              </ul>
            </div>
          )}

          <p style={{ margin: '13px 0 0', fontSize: 12.5, lineHeight: 1.7, color: 'var(--text-disabled)', ...font }}>
            {c.sequence}
          </p>

          <button
            onClick={onVerify}
            style={{
              marginTop: 16,
              width: '100%',
              background: AMBER_INK,
              color: '#FFF',
              fontSize: 14.5,
              fontWeight: 800,
              padding: '13px 20px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              ...font,
            }}
          >{c.cta}</button>
        </>
      ) : readyToSubmit ? (
        <>
          <p style={{ margin: '9px 0 0', fontSize: 13.5, lineHeight: 1.75, color: 'var(--text-secondary)', ...font }}>
            {c.readyBody}
          </p>
          <p style={{ margin: '13px 0 0', fontSize: 12.5, lineHeight: 1.7, color: 'var(--text-disabled)', ...font }}>
            {c.sequence}
          </p>
          <button
            onClick={onVerify}
            style={{
              marginTop: 15,
              background: INK,
              color: '#FAF8F5',
              fontSize: 13.5,
              fontWeight: 700,
              padding: '11px 20px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              ...font,
            }}
          >{c.readyCta}</button>
        </>
      ) : (
        <p style={{ margin: '9px 0 0', fontSize: 13.5, lineHeight: 1.75, color: 'var(--text-secondary)', ...font }}>
          {c.reviewBody}
        </p>
      )}
      </div>
    </div>
  );
}
